/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useTranslation} from 'react-i18next'
import {TFunction} from 'i18next'
import {useMemo} from 'react'
import {OfferStatus} from 'types/enums/OfferStatus'
import {ReservationStatus} from 'types/enums/ReservationStatus'
import {Offer, OfferCreateReservationResponse} from 'types/entities/Offer'
import type {Reservation} from 'types/entities/Reservation'
import {OfferCalculation} from 'types/entities/OfferCalculation'
import {Driver} from 'types/entities/Driver'
import {Row, Table} from '@tanstack/react-table'
import UiTableHeaderLabel from 'components/Ui/UiTableHeaderLabel'
import {UiTableCellTextPrimary, UiTableCellTextSecondary} from 'components/Ui/UiTableCellText'
import Dates from 'utils/Dates'
import {getShortNameFromId} from 'utils/reservationUtils'
import {getPackageCategoryClassName} from 'utils/packageUtils'
import {PackageCategory} from 'types/enums/PackageCategory'
import UiPackagePill from 'components/Ui/UiPackagePill'
import UiPill from 'components/Ui/UiPill'
import type {PackagesCart} from 'types/entities/PackagesCart'
import UiOfferStatusPill from 'components/Ui/UiOfferStatusPill'
import {currencyNumberFormat, getOfferPersonsCount, getPackagesCartPersonsCount, numberRangeColumnFilter} from 'utils/utility'
import clsx from 'clsx'
import {UiTableColumnVisibilityToggle} from 'components/Ui/UiTableColumnVisibilityToggle'
import {driverName} from 'utils/driverUtils'
import {getCompanyDisplayName} from 'utils/companyUtils'
import UiTableRowActionsContainer from 'components/Ui/UiTableRowActionsContainer'
import UiTableRowActionButton from 'components/Ui/UiTableRowActionButton'
import {UI_TABLE_ACTIONS_CELL_CLASS} from 'components/Ui/UiTableSharedStyles'
import UiCompanyTypePill from 'components/Ui/UiCompanyTypePill'
import {CompanyBranchType} from 'types/enums/CompanyBranchType'
import {tableActionsMeta} from 'utils/table/tableColumnWidths'
import {PackageItemItemType} from 'types/enums/PackageItemItemType'

/**
 * The Offer header must represent the selected target calculation only.
 * Credits, upsells and other non-ticket lines intentionally do not count as
 * reserved people.
 */
export const getTargetOfferTicketPersonsCount = (offer?: Offer | null): number => {
    if (!offer) {
        return 0
    }

    const targetCalculation = offer.calculations?.find((calculation) => calculation.is_target)
    const targetCart = targetCalculation?.packagesCart ?? offer.packagesCart
    const items = targetCart?.packagesCartItems

    if (!Array.isArray(items)) {
        return getPackagesCartPersonsCount(targetCart)
    }

    return items
        .filter((item) => item.packageItem?.type === PackageItemItemType.PackageItem)
        .reduce(
            (total, item) =>
                total +
                Number(item.drivers_count_adult ?? 0) +
                Number(item.drivers_count_child ?? 0),
            0
        )
}

const formatMinutesAsHourLabel = (minutes: number): string | null => {
    if (!Number.isFinite(minutes) || minutes <= 0) {
        return null
    }

    const hours = minutes / 60
    const roundedHours = Number(hours.toFixed(2))
    return `${roundedHours}h`
}

const packageItemNameContainsKarting = (name: unknown): boolean => {
    if (!name || typeof name !== 'object') {
        return false
    }

    return Object.values(name as Record<string, unknown>).some((value) => {
        if (typeof value !== 'string') {
            return false
        }
        return value.toLowerCase().includes('kart')
    })
}

type HoursBreakdownMeta = {
    label: string
    hasKarting: boolean
    kartingHours: number
    nonKartingHours: number
}

type StayBreakdown = HoursBreakdownMeta

const normalizeHoursValue = (value: unknown): number => {
    const parsed = Number(value)
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0
    }
    return Number(parsed.toFixed(2))
}

type ApiStayBreakdownEntry = {
    hours?: unknown
}

type ApiStayBreakdownPayload = {
    karting?: ApiStayBreakdownEntry
    non_karting?: ApiStayBreakdownEntry
}

const parseApiStayBreakdownHours = (packagesCart?: PackagesCart | null): Pick<StayBreakdown, 'kartingHours' | 'nonKartingHours'> | null => {
    const source = packagesCart as (PackagesCart & { hours_breakdown?: unknown; duration_breakdown?: unknown }) | null | undefined
    const candidate = source?.hours_breakdown ?? source?.duration_breakdown ?? null
    if (!candidate || typeof candidate !== 'object') {
        return null
    }

    const parsed = candidate as ApiStayBreakdownPayload

    const kartingHours = normalizeHoursValue(parsed.karting?.hours)
    const nonKartingHours = normalizeHoursValue(parsed.non_karting?.hours)

    if (kartingHours <= 0 && nonKartingHours <= 0) {
        return null
    }

    return {
        kartingHours,
        nonKartingHours,
    }
}

export const getPackagesCartHoursBreakdownMeta = (
    packagesCart?: PackagesCart | null
): StayBreakdown => {
    const apiStayBreakdown = parseApiStayBreakdownHours(packagesCart)
    if (apiStayBreakdown) {
        const parts: string[] = []
        if (apiStayBreakdown.kartingHours > 0) {
            parts.push(`${apiStayBreakdown.kartingHours}h`)
        }
        if (apiStayBreakdown.nonKartingHours > 0) {
            parts.push(`${apiStayBreakdown.nonKartingHours}h`)
        }

        return {
            label: parts.join(', ') || '-',
            hasKarting: apiStayBreakdown.kartingHours > 0,
            kartingHours: apiStayBreakdown.kartingHours,
            nonKartingHours: apiStayBreakdown.nonKartingHours,
        }
    }

    if (!packagesCart?.packagesCartItems || packagesCart.packagesCartItems.length === 0) {
        return {
            label: '-',
            hasKarting: false,
            kartingHours: 0,
            nonKartingHours: 0,
        }
    }

    const hasKarting = packagesCart.packagesCartItems.some((item) => {
        const raceCountTotal = Number(item.packageItem?.article?.race_count_total ?? 0)
        if (raceCountTotal > 0) {
            return true
        }

        return packageItemNameContainsKarting(item.packageItem?.name)
    })

    const bucket = new Map<number, { label: string; tickets: number }>()

    packagesCart.packagesCartItems.forEach((item) => {
        const durationMinutes = Number(item.packageItem?.duration ?? item.package?.duration ?? 0)
        const label = formatMinutesAsHourLabel(durationMinutes)

        if (!label) {
            return
        }

        const ticketCount = Math.max(1, Number(item.drivers_count_adult ?? 0) + Number(item.drivers_count_child ?? 0))
        const existing = bucket.get(durationMinutes)

        if (!existing) {
            bucket.set(durationMinutes, {
                label,
                tickets: ticketCount,
            })
            return
        }

        existing.tickets += ticketCount
        bucket.set(durationMinutes, existing)
    })

    if (bucket.size === 0) {
        return {
            label: '-',
            hasKarting,
            kartingHours: 0,
            nonKartingHours: 0,
        }
    }

    const label = [...bucket.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([, value]) => `${value.label} x${value.tickets}`)
        .join(', ')

    return {
        label,
        hasKarting,
        kartingHours: 0,
        nonKartingHours: 0,
    }
}

export const getPackagesCartHoursBreakdown = (packagesCart?: PackagesCart | null): string => {
    return getPackagesCartHoursBreakdownMeta(packagesCart).label
}

export interface OfferStatusDescriptor {
    value: OfferStatus
    label: string
}

export const extractReservationInfo = (response: OfferCreateReservationResponse) => {
    const reservationId =
        response?.packagesCart?.reservation_id ||
        response?.data?.packagesCart?.reservation_id ||
        response?.offer?.packagesCart?.reservation_id ||
        response?.data?.offer?.packagesCart?.reservation_id ||
        response?.reservation_id ||
        response?.data?.reservation_id

    const reservationGuid =
        response?.guid ||
        response?.data?.guid ||
        response?.reservation?.guid ||
        response?.data?.reservation?.guid

    return { reservationId, reservationGuid }
}

export const getActiveOfferCalculations = (calculations?: OfferCalculation[] | null): OfferCalculation[] => {
    return (calculations ?? []).filter((calculation) => !calculation?.deleted_at)
}

export const getDeletedOfferCalculations = (calculations?: OfferCalculation[] | null): OfferCalculation[] => {
    return (calculations ?? []).filter((calculation) => !!calculation?.deleted_at)
}

const getOfferCalculationNameString = (name?: string | null): string => {
    if (typeof name === 'string') {
        return name.trim()
    }

    return ''
}

const getCalculationNameRaw = (calculation: OfferCalculation): string =>
    getOfferCalculationNameString(calculation.name)

const getCalculationNumericName = (calculation: OfferCalculation): number | null => {
    const rawName = getCalculationNameRaw(calculation)
    if (!/^\d+$/.test(rawName)) {
        return null
    }

    const parsed = Number.parseInt(rawName, 10)
    return Number.isFinite(parsed) ? parsed : null
}

/** Sorts the given calculations for display. Does not filter by deleted_at. */
export const sortOfferCalculationsForDisplay = (
    calculations?: OfferCalculation[] | null
): OfferCalculation[] => {
    return [...(calculations ?? [])].sort((left, right) => {
        const leftName = getCalculationNumericName(left)
        const rightName = getCalculationNumericName(right)

        if (leftName !== null && rightName !== null && leftName !== rightName) {
            return leftName - rightName
        }

        if (leftName !== null && rightName === null) {
            return -1
        }

        if (leftName === null && rightName !== null) {
            return 1
        }

        return left.id - right.id
    })
}

export const getOfferCalculationLabel = (
    calculation: OfferCalculation,
    t: (key: string) => string,
    fallbackIndex?: number
): string => {
    const rawName = getCalculationNameRaw(calculation)

    if (/^\d+$/.test(rawName)) {
        return `${t('Calculation')} ${rawName}`
    }

    if (rawName) {
        return rawName
    }

    if (fallbackIndex !== undefined) {
        return `${t('Calculation')} ${fallbackIndex}`
    }

    return t('Calculation')
}

export const getOfferTargetPackagesCartId = (offer?: Offer | null): number | null =>
    offer?.packages_cart_id ?? offer?.packagesCart?.id ?? null

export const getTargetOfferCalculation = (
    calculations?: OfferCalculation[] | null,
    offer?: Offer | null
): OfferCalculation | null => {
    const active = sortOfferCalculationsForDisplay(getActiveOfferCalculations(calculations))
    if (!active.length) {
        return null
    }

    const explicitTarget = active.find((calculation) => calculation.is_target)
    if (explicitTarget) {
        return explicitTarget
    }

    const targetCartId = getOfferTargetPackagesCartId(offer)
    if (targetCartId) {
        const cartMatch = active.find(
            (calculation) =>
                calculation.packages_cart_id === targetCartId ||
                calculation.packagesCart?.id === targetCartId
        )
        if (cartMatch) {
            return cartMatch
        }
    }

    return active[0] ?? null
}

export const isTargetOfferCalculation = (offer: Offer, calculation: OfferCalculation): boolean =>
    getTargetOfferCalculation(offer.calculations, offer)?.id === calculation.id

export const getNextOfferCalculationIndex = (calculations?: OfferCalculation[] | null): number => {
    return getActiveOfferCalculations(calculations).length + 1
}

export const getOfferCalculationCount = (offer?: Offer | null): number => {
    if (!offer) {
        return 0
    }

    if (typeof offer.calculations_count === 'number' && Number.isFinite(offer.calculations_count)) {
        // Expanded calculations can prove that the aggregate is stale; a partial list cannot disprove it.
        return Math.max(0, Math.trunc(offer.calculations_count), getActiveOfferCalculations(offer.calculations).length)
    }

    const activeCalculations = getActiveOfferCalculations(offer.calculations)
    if (activeCalculations.length > 0) {
        return activeCalculations.length
    }

    return offer.packagesCart ? 1 : 0
}

export const shouldShowOfferCalculationSubRows = (offer?: Offer | null): boolean =>
    getOfferCalculationCount(offer) > 1

export const getOfferPrimaryPackagesCart = (offer?: Offer | null) => {
    if (!offer) {
        return null
    }

    const activeCalculations = getActiveOfferCalculations(offer.calculations)
    if (activeCalculations.length === 1) {
        return activeCalculations[0].packagesCart ?? offer.packagesCart ?? null
    }

    return offer.packagesCart ?? getTargetOfferCalculation(offer.calculations, offer)?.packagesCart ?? null
}

export const getOffersCalculationRevenueRange = (offers?: Offer[] | null) => {
    const prices: number[] = []

    ;(offers ?? []).forEach((offer) => {
        const activeCalculations = sortOfferCalculationsForDisplay(
            getActiveOfferCalculations(offer.calculations)
        )

        if (activeCalculations.length > 0) {
            activeCalculations.forEach((calculation) => {
                const calculationPrice = calculation.packagesCart?.price ?? calculation.price_total ?? 0
                prices.push(calculationPrice)
            })
            return
        }

        if (offer.packagesCart) {
            prices.push(offer.packagesCart.price ?? 0)
        }
    })

    if (prices.length === 0) {
        return {
            minRevenue: 0,
            maxRevenue: 0,
        }
    }

    return {
        minRevenue: Math.min(...prices),
        maxRevenue: Math.max(...prices),
    }
}

const OFFER_DETAIL_RETURN_TO_STORAGE_KEY = 'offer_detail_return_to'

const normalizeOfferReturnTo = (path?: string | null): string | null => {
    if (!path) return null
    const trimmedPath = path.trim()
    if (!trimmedPath.startsWith('/')) return null
    return trimmedPath
}

export const resolveOfferReturnTo = (locationState?: { returnTo?: string } | null): string => {
    const fromState = normalizeOfferReturnTo(locationState?.returnTo)

    if (fromState) {
        if (typeof window !== 'undefined') {
            try {
                window.sessionStorage.setItem(OFFER_DETAIL_RETURN_TO_STORAGE_KEY, fromState)
            } catch {
                // Ignore storage errors (private mode, quotas, etc.)
            }
        }
        return fromState
    }

    if (typeof window !== 'undefined') {
        try {
            const fromStorage = normalizeOfferReturnTo(
                window.sessionStorage.getItem(OFFER_DETAIL_RETURN_TO_STORAGE_KEY)
            )
            if (fromStorage) {
                return fromStorage
            }
        } catch {
            // Ignore storage errors
        }
    }

    return '/offers'
}

export const buildOfferPdfFilename = (
    offerNumber?: number | null,
    calculationName?: string | null,
    fallbackCalculationIndex: number = 1
): string => {
    const offerSuffix =
        typeof offerNumber === 'number' && Number.isFinite(offerNumber)
            ? String(offerNumber).padStart(3, '0')
            : 'offer'

    const calculationRaw = (calculationName || '').trim()
    const fallbackSuffix =
        Number.isFinite(fallbackCalculationIndex) && fallbackCalculationIndex > 0
            ? String(fallbackCalculationIndex)
            : '1'
    const calculationSuffix = /^\d+$/.test(calculationRaw)
        ? calculationRaw
        : fallbackSuffix

    return `${offerSuffix}-${calculationSuffix}.pdf`
}

export const extractOfferPdfResponse = (response: unknown): { offer?: string; downloadAs?: string } => {
    if (!response || typeof response !== 'object') {
        return {}
    }

    const payload = response as {
        offer?: unknown
        download_as?: unknown
        data?: {
            offer?: unknown
            download_as?: unknown
            data?: {
                offer?: unknown
                download_as?: unknown
            }
        }
    }

    const offerCandidate =
        payload.offer ??
        payload.data?.offer ??
        payload.data?.data?.offer

    const downloadAsCandidate =
        payload.download_as ??
        payload.data?.download_as ??
        payload.data?.data?.download_as

    return {
        offer: typeof offerCandidate === 'string' ? offerCandidate : undefined,
        downloadAs: typeof downloadAsCandidate === 'string' ? downloadAsCandidate : undefined,
    }
}

export const extractOfferConfirmationPdfResponse = (response: unknown): { pdf?: string; downloadAs?: string } => {
    if (!response || typeof response !== 'object') {
        return {}
    }

    const payload = response as {
        pdf?: unknown
        download_as?: unknown
        data?: {
			pdf?: unknown
            download_as?: unknown
        }
    }

    const offerConfirmationCandidate =
        payload.pdf ??
        payload.data?.pdf

    const downloadAsCandidate =
        payload.download_as ??
        payload.data?.download_as

    return {
        pdf: typeof offerConfirmationCandidate === 'string' ? offerConfirmationCandidate : undefined,
        downloadAs: typeof downloadAsCandidate === 'string' ? downloadAsCandidate : undefined,
    }
}

export {resolveOfferPdfDownload} from './offerPdfDownload'

const INACTIVE_RESERVATION_STATUSES = new Set<string>([
    ReservationStatus.Deleted,
    ReservationStatus.Cancelled,
])

export const isActiveOfferReservation = (reservation?: Reservation | null): boolean => {
    if (!reservation?.id) {
        return false
    }

    const status = String(reservation.status || '').toLowerCase()
    return !INACTIVE_RESERVATION_STATUSES.has(status)
}

export const hasOfferReservation = (offer: Offer): boolean =>
    // Only trust offer.reservation.
    // - packagesCart.reservation_id exists on brand-new offers (draft cart) before booking
    // - nested packagesCart.reservation is often rehydrated stale by item add/update/delete
    isActiveOfferReservation(offer.reservation)

export const getOfferEffectiveRevenue = (offer?: Offer | null): number | null => {
    const value = offer?.finishedReservation?.total_invoice
    if (value === null || value === undefined || value === '') {
        return null
    }

    const revenue = Number(value)
    return Number.isFinite(revenue) ? Math.max(0, revenue) : null
}

export const getOfferDeletableReservationId = (offer: Offer): number | null => {
    if (isActiveOfferReservation(offer.reservation)) {
        return offer.reservation!.id
    }

    if (isActiveOfferReservation(offer.packagesCart?.reservation)) {
        return offer.packagesCart!.reservation!.id
    }

    const cartReservationId = offer.packagesCart?.reservation_id
    if (!cartReservationId) {
        return null
    }

    if (hasOfferReservation(offer)) {
        return cartReservationId
    }

    const statusesWithCartReservationLink = new Set<OfferStatus>([
        OfferStatus.Accepted,
        OfferStatus.Finished,
        OfferStatus.Sent,
    ])

    if (offer.status && statusesWithCartReservationLink.has(offer.status)) {
        return cartReservationId
    }

    return null
}

/**
 * Offer is fully read-only when Accepted or Rejected.
 * Reconfirm is a separate status and intentionally remains editable.
 */
export const isOfferEditLocked = (status?: OfferStatus | string | null): boolean =>
    status === OfferStatus.Accepted || status === OfferStatus.Rejected

export const getOfferEditLockedTooltip = (
    t: TFunction,
    status?: OfferStatus | string | null
): string => {
    if (status === OfferStatus.Rejected) {
        return t('Disabled because offer is rejected') as string
    }
    return t(
        'Disabled because offer is accepted. Switch status to Reconfirm to enable changes.'
    ) as string
}

/**
 * Offer statuses that mirror opportunity locks (Zusage / Completed):
 * Accepted and Finished are driven by booking/system flows, not manual mass edits.
 */
export const SYSTEM_ONLY_OFFER_STATUSES = new Set<OfferStatus>([
    OfferStatus.Accepted,
    OfferStatus.Finished,
])

export const isManuallySelectableOfferStatus = (
    status: OfferStatus | string,
    currentStatus?: OfferStatus | string | null
): boolean => {
    if (!SYSTEM_ONLY_OFFER_STATUSES.has(status as OfferStatus)) {
        return true
    }
    return currentStatus === status
}

export const getSystemOnlyOfferStatusReason = (
    t: TFunction,
    status: OfferStatus | string
): string => {
    if (status === OfferStatus.Accepted) {
        return t('Accepted status can only be set by creating a booking from an offer.') as string
    }
    if (status === OfferStatus.Finished) {
        return t('Completed status can only be set by the system.') as string
    }
    return t('This status cannot be set manually.') as string
}

export const useOfferStatusOptions = () => {
    const { t } = useTranslation()

    const options = useMemo<Array<{ label: string; value: OfferStatus }>>(() => [
        { label: t('Draft'), value: OfferStatus.Draft },
        { label: t('New'), value: OfferStatus.New },
        { label: t('Sent'), value: OfferStatus.Sent },
        { label: t('Accepted'), value: OfferStatus.Accepted },
        { label: t('Reconfirm'), value: OfferStatus.Reconfirm },
        { label: t('Finished'), value: OfferStatus.Finished },
        { label: t('Rejected'), value: OfferStatus.Rejected },
        { label: t('Expired'), value: OfferStatus.Expired },
    ], [t])

    const map = useMemo(() => {
        const statusMap = {} as Record<OfferStatus, OfferStatusDescriptor>
        options.forEach(option => { statusMap[option.value] = option })
        return statusMap
    }, [options])

    const getStatusDescriptor = (value: OfferStatus) => map[value] ?? map[OfferStatus.Draft]

    return { options, map, getStatusDescriptor }
}

const formatOfferEventTime = (offer: Offer) => {
    const packagesCart = offer.packagesCart
    if (packagesCart?.hour === undefined || packagesCart?.minute === undefined) {
        return '-'
    }
    return `${String(packagesCart.hour).padStart(2, '0')}:${String(packagesCart.minute).padStart(2, '0')}`
}

export interface UseOfferColumnsOptions {
    sellerById: (sellerId: number) => Driver | null | undefined
    onEdit: (offer: Offer) => void
    onDelete: (offer: Offer) => void
}

export const useOfferColumns = ({ sellerById, onEdit, onDelete }: UseOfferColumnsOptions) => {
    const { t } = useTranslation()

    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('ID')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => (
                    <UiTableCellTextPrimary>{row.original.id}</UiTableCellTextPrimary>
                ),
            },
            {
                accessorFn: (row: Offer) => {
                    const seller = row.seller ?? sellerById(row.seller_id)
                    return seller ? driverName(seller, true) : '-'
                },
                id: 'seller_id',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Seller')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => {
                    const seller = row.original.seller ?? sellerById(row.original.seller_id)
                    return <UiTableCellTextPrimary>{seller ? driverName(seller, true) : '-'}</UiTableCellTextPrimary>
                },
            },
            {
                accessorFn: (row: Offer) => row.number ?? null,
                id: 'offer_number',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Offer number')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => (
                    <UiTableCellTextPrimary>{row.original?.number ?? '---'}</UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'created_at',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Created')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => (
                    <UiTableCellTextSecondary>
                        {Dates.date(row.original?.created_at) || Dates.date(row.original?.packagesCart?.created_at) || '-'}
                    </UiTableCellTextSecondary>
                ),
            },
            {
                accessorKey: 'updated_at',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Updated')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => (
                    <UiTableCellTextSecondary>
                        {Dates.date(row.original?.updated_at) || Dates.date(row.original?.packagesCart?.updated_at) || '-'}
                    </UiTableCellTextSecondary>
                ),
            },
            {
                accessorFn: (row: Offer) => getCompanyDisplayName(row.company) || null,
                id: 'company_name',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Company')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => (
                    <UiTableCellTextPrimary className="break-words">
                        {getCompanyDisplayName(row.original?.company) || '-'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorFn: (row: Offer) => row.company?.type ?? null,
                id: 'company.type',
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Type')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => {
                    const companyType = row.original.company?.type

                    if (!companyType) {
                        return <UiTableCellTextPrimary>-</UiTableCellTextPrimary>
                    }

                    return <UiCompanyTypePill type={companyType} />
                },
            },
            {
                accessorFn: (row: Offer) =>
                    getShortNameFromId(row.packagesCart?.category) ?? row.packagesCart?.category ?? null,
                id: 'package',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Ticket type')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => {
                    const offer = row.original
                    const packagesCart: PackagesCart | null | undefined = offer.packagesCart
                    const packageName = packagesCart?.category

                    return (
                        <UiTableCellTextPrimary>
                            <UiPackagePill
                                className={getPackageCategoryClassName(packageName)}
                                value={getShortNameFromId(packageName)}
                            />
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorFn: (row: Offer) => row.packagesCart?.date ?? null,
                id: 'date',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Event date')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => {
                    const packagesCart = row.original.packagesCart
                    return (
                        <UiTableCellTextPrimary>{packagesCart?.date ? Dates.date(packagesCart.date) : '-'}</UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorFn: (row: Offer) => {
                    const packagesCart = row.packagesCart
                    if (packagesCart?.hour === undefined || packagesCart?.minute === undefined) {
                        return null
                    }
                    return `${String(packagesCart.hour).padStart(2, '0')}:${String(packagesCart.minute).padStart(2, '0')}`
                },
                id: 'event_time',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Event time')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => <UiTableCellTextPrimary>{formatOfferEventTime(row.original)}</UiTableCellTextPrimary>,
            },
            {
                accessorFn: (row: Offer) => getOfferPersonsCount(row),
                id: 'persons',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                filterFn: numberRangeColumnFilter,
                header: () => <UiTableHeaderLabel>{t('Persons')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => {
                    const totalPersons = getOfferPersonsCount(row.original)
                    return <UiTableCellTextPrimary>{totalPersons > 0 ? totalPersons : '-'}</UiTableCellTextPrimary>
                },
            },
            {
                accessorKey: 'status',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Offer Status')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => <UiOfferStatusPill status={row.original.status} />,
            },
            {
                accessorKey: 'has_reservation',
                enableSorting: true,
                enableColumnFilter: false,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Has reservation')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => (
                    <UiTableCellTextPrimary>
                        <UiPill
                            className={
                                hasOfferReservation(row.original)
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-gray-100 text-gray-700'
                            }
                        >
                            {hasOfferReservation(row.original) ? t('Yes') : t('No')}
                        </UiPill>
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'value',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Revenue')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Offer> }) => (
                    <UiTableCellTextPrimary>
                        {row.original?.packagesCart ? currencyNumberFormat(row.original?.packagesCart.price) : '---'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'actions',
                ...tableActionsMeta(2),
                enableSorting: false,
                enableColumnFilter: false,
                header: ({ table }: { table: Table<Offer> }) => (
                    <div className={clsx('flex flex-row items-center leading-tight justify-end h-full')}>
                        <UiTableColumnVisibilityToggle table={table} />
                    </div>
                ),
                cell: ({ row }: { row: Row<Offer> }) => {
                    const offer = row.original
                    return (
                        <UiTableRowActionsContainer className={UI_TABLE_ACTIONS_CELL_CLASS}>
                            <UiTableRowActionButton
                                variant={'edit'}
                                title={t('Edit')}
                                onClick={() => onEdit(offer)}
                            />
                            <UiTableRowActionButton
                                variant={'delete'}
                                title={t('Delete')}
                                onClick={() => onDelete(offer)}
                            />
                        </UiTableRowActionsContainer>
                    )
                },
            },
        ],
        [t, sellerById, onEdit, onDelete]
    )

    return {
        columns,
    }
}

export const getOffersFooterTotals = (offers: Offer[]) => {
    return offers.reduce(
        (acc, offer) => {
            acc.totalRevenue += offer?.packagesCart?.price || 0
            acc.totalPersons += getOfferPersonsCount(offer)
            return acc
        },
        { totalRevenue: 0, totalPersons: 0 }
    )
}

// ─── Flat-row type for offers table with inline calculations ─────────────────

export type OffersTableRow = {
    id: string
    rowType: 'offer' | 'calculation'
    offer: Offer
    tags: string[]
    calculation: OfferCalculation | null
    calculationIndex: number | null
    offer_id: number
    offer_number: number | null
    seller_id: number
    company_name: string
    company_type: CompanyBranchType | null
    package: string | null
    duration_breakdown: string
    duration_has_karting: boolean
    duration_karting_hours: number
    duration_non_karting_hours: number
    date: string | null
    event_time: string | null
    persons: number
    status: OfferStatus | string
    has_reservation: boolean
    value: number
    effective_revenue: number | null
    created_at: string | null
    updated_at: string | null
    calculations_count: number
    calculation_label: string | null
    is_target: boolean
    valid_until: string | null
}

const normalizeFlatRowEventDate = (date: unknown): string | null => {
    if (typeof date !== 'string') return null

    const normalized = date.trim()
    if (!normalized || normalized === '0' || normalized.startsWith('0000-00-00')) {
        return null
    }

    return normalized
}

const formatFlatRowEventTime = (
    hour?: number | null,
    minute?: number | null,
    hasEventDate = true
): string | null => {
    if (!hasEventDate) return null
    if (hour === undefined || hour === null || minute === undefined || minute === null) return null
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

export const buildOffersTableFlatRows = (offers: Offer[], t: (key: string) => string): OffersTableRow[] => {
    const rows: OffersTableRow[] = []
    for (const offer of offers) {
        const primaryCart = getOfferPrimaryPackagesCart(offer)
        const primaryEventDate = normalizeFlatRowEventDate(primaryCart?.date ?? offer.date)
        const primaryDurationBreakdown = getPackagesCartHoursBreakdownMeta(primaryCart)
        const activeCalculations = sortOfferCalculationsForDisplay(
            getActiveOfferCalculations(offer.calculations)
        )
        const calculationsCount = getOfferCalculationCount(offer)

        rows.push({
            id: `offer-${offer.id}`,
            rowType: 'offer',
            offer,
            tags: offer.tags ?? [],
            calculation: null,
            calculationIndex: null,
            offer_id: offer.id,
            offer_number: offer.number ?? null,
            seller_id: offer.seller_id,
            company_name: getCompanyDisplayName(offer.company) || '-',
            company_type: offer.company?.type ?? null,
            package: primaryCart?.category ?? null,
            duration_breakdown: primaryDurationBreakdown.label,
            duration_has_karting: primaryDurationBreakdown.hasKarting,
            duration_karting_hours: primaryDurationBreakdown.kartingHours,
            duration_non_karting_hours: primaryDurationBreakdown.nonKartingHours,
            date: primaryEventDate,
            event_time: formatFlatRowEventTime(
                primaryCart?.hour,
                primaryCart?.minute,
                !!primaryEventDate
            ),
            persons: getPackagesCartPersonsCount(primaryCart),
            status: offer.status ?? '-',
            has_reservation: hasOfferReservation(offer),
            value: primaryCart?.price ?? 0,
            effective_revenue: getOfferEffectiveRevenue(offer),
            created_at: offer.created_at ?? primaryCart?.created_at ?? null,
            updated_at: offer.updated_at ?? primaryCart?.updated_at ?? null,
            calculations_count: calculationsCount,
            calculation_label: null,
            is_target: false,
            valid_until: offer.valid_until ?? null,
        })

        if (!shouldShowOfferCalculationSubRows(offer)) {
            continue
        }

        for (let i = 0; i < activeCalculations.length; i++) {
            const calc = activeCalculations[i]
            const calcCart = calc.packagesCart ?? null
            const calculationEventDate = normalizeFlatRowEventDate(calc.date ?? calcCart?.date)
            const calculationDurationBreakdown = getPackagesCartHoursBreakdownMeta(calcCart)
            rows.push({
                id: `offer-${offer.id}-calc-${calc.id}`,
                rowType: 'calculation',
                offer,
                tags: offer.tags ?? [],
                calculation: calc,
                calculationIndex: i,
                offer_id: offer.id,
                offer_number: offer.number ?? null,
                seller_id: offer.seller_id,
                company_name: getCompanyDisplayName(offer.company) || '-',
                company_type: offer.company?.type ?? null,
                package: calcCart?.category ?? null,
                duration_breakdown: calculationDurationBreakdown.label,
                duration_has_karting: calculationDurationBreakdown.hasKarting,
                duration_karting_hours: calculationDurationBreakdown.kartingHours,
                duration_non_karting_hours: calculationDurationBreakdown.nonKartingHours,
                date: calculationEventDate,
                event_time: formatFlatRowEventTime(
                    calc.hour ?? calcCart?.hour,
                    calc.minute ?? calcCart?.minute,
                    !!calculationEventDate
                ),
                persons: getPackagesCartPersonsCount(calcCart),
                status: offer.status ?? '-',
                has_reservation: hasOfferReservation(offer),
                value: calcCart?.price ?? calc.price_total ?? 0,
                effective_revenue: null,
                created_at: calc.created_at ?? offer.created_at ?? null,
                updated_at: calc.updated_at ?? offer.updated_at ?? null,
                calculations_count: calculationsCount,
                calculation_label: getOfferCalculationLabel(calc, t, i + 1),
                is_target: isTargetOfferCalculation(offer, calc),
                valid_until: offer.valid_until ?? null,
            })
        }
    }
    return rows
}

export { useOffersTableFlatColumns } from './offersColumns'
export type { UseOffersTableFlatColumnsOptions } from './offersColumns'

